import React from "react";
import WebsiteLayout from "./WebsiteLayout";
import { useScrollReveal } from "./useAnimations";
import { Server, Cloud, Plug, Database, Shield, Cpu, GitBranch, Layers, Check } from "lucide-react";

const TECH_SECTIONS = [
  { icon: <Server size={28} />, label: "Platform Architecture", title: "Modular, Event-Driven Design",
    desc: "Tychi is built on a modular architecture where each component operates as an independent service while maintaining deep integration through shared data contracts and event-driven communication.",
    points: ["Service-oriented architecture with clear domain boundaries", "Event-driven communication between platform modules", "Independent module deployment and versioning", "Consistent API contracts across all services"] },
  { icon: <Cloud size={28} />, label: "Cloud Infrastructure", title: "Scalable & Reliable Deployment",
    desc: "Tychi operates on enterprise-grade cloud infrastructure designed for financial workloads with containerized deployment, automated scaling, and multi-region redundancy.",
    points: ["Containerized microservices with automated orchestration", "Horizontal scaling based on operational demand", "Multi-region deployment for disaster recovery", "99.9% uptime SLA with proactive monitoring"] },
  { icon: <Plug size={28} />, label: "API Architecture", title: "Integration-Ready Platform",
    desc: "Every capability within Tychi is accessible through well-documented, versioned APIs enabling integration with custodians, trading platforms, and compliance systems.",
    points: ["RESTful API design with comprehensive documentation", "Versioned endpoints for backward compatibility", "Webhook support for real-time event notifications", "Secure API authentication with token-based access"] },
  { icon: <Database size={28} />, label: "Data Integrity", title: "Financial-Grade Data Management",
    desc: "Tychi's data layer implements strict integrity controls, complete audit trails, and AI-assisted data validation ensuring every record is accurate, traceable, and compliant.",
    points: ["ACID-compliant transaction processing", "Immutable audit logs with full change history", "AI-assisted data validation at every entry point", "Structured data archival with instant retrieval"] },
];

export default function TechnologyPage() {
  useScrollReveal();
  return (
    <WebsiteLayout>
      <div className="w-banner-dark">
        <section className="w-banner w-reveal" data-testid="technology-banner" style={{ paddingBottom: "4rem" }}>
          <div className="w-label">Technology</div>
          <h1 className="w-title" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
            Engineered for <strong>Financial Operations</strong>
          </h1>
          <p className="w-subtitle" style={{ maxWidth: 560 }}>
            AI-enabled, purpose-built infrastructure delivering reliability, scalability, and security at every layer.
          </p>
        </section>
      </div>

      <section className="w-section" style={{ paddingTop: "3rem" }}>
        <div className="w-arch w-reveal-scale">
          {[
            { icon: <Layers size={24} />, t: "Application Layer", d: "React & FastAPI" },
            { icon: <GitBranch size={24} />, t: "Logic Layer", d: "Business Rules Engine" },
            { icon: <Database size={24} />, t: "Data Layer", d: "Financial Data Store" },
            { icon: <Shield size={24} />, t: "Security Layer", d: "Encryption & Auth" },
            { icon: <Cpu size={24} />, t: "Processing Layer", d: "Computation Engine" },
            { icon: <Cloud size={24} />, t: "Infrastructure", d: "Cloud Deployment" },
          ].map((t, i) => (
            <div key={i} className="w-arch-item">
              <div style={{ color: "var(--w-blue)" }}>{t.icon}</div>
              <h4>{t.t}</h4><p>{t.d}</p>
            </div>
          ))}
        </div>
      </section>

      {TECH_SECTIONS.map((s, i) => (
        <section key={i} className={i % 2 === 0 ? "w-section-full w-section-surface" : "w-section"}>
          <div className={i % 2 === 0 ? "w-section" : ""} style={i % 2 === 0 ? { paddingTop: "6rem", paddingBottom: "6rem" } : {}}>
            <div className={`w-split ${i % 2 === 1 ? "reverse" : ""}`}>
              <div className={i % 2 === 0 ? "w-reveal-left" : "w-reveal-right"}>
                <div className="w-label">{s.label}</div>
                <h2 className="w-title" style={{ fontSize: "clamp(1.75rem, 3vw, 2.25rem)" }}>{s.title}</h2>
                <p style={{ color: "var(--w-text-muted)", lineHeight: 1.75, marginBottom: "1.5rem" }}>{s.desc}</p>
                <ul className="w-features">
                  {s.points.map((p, j) => <li key={j}><span className="check-icon"><Check size={12} /></span> {p}</li>)}
                </ul>
              </div>
              <div className={`${i % 2 === 0 ? "w-reveal-right" : "w-reveal-left"} w-delay-2`} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 200, height: 200, borderRadius: "50%", border: "1px solid var(--w-border)", background: "var(--w-surface)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ color: "var(--w-blue)" }}>{React.cloneElement(s.icon, { size: 64 })}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </WebsiteLayout>
  );
}
